import {
  type ColumnDef,
  PaginationState,
  SortingState,
  createColumnHelper,
} from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";

import { useNotification } from "@providers/NotificationProvider/useNotification";

import { NotificationTypeMappingText } from "@components/Texts";

import { Box } from "@design-system/Box/Box";
import { Dropdown } from "@design-system/Dropdown/Dropdown";
import { IconButton } from "@design-system/IconButton/IconButton";
import { Table } from "@design-system/Table/Table";
import { Text } from "@design-system/Text/Text";

import { usePaginationWithParams } from "@utils/hooks/usePaginationParams";
import { getNotificationReasonLabel } from "@utils/notificationReasons";

import {
  ResourceGroupNotificationsQuery,
  useResourceGroupNotificationsQuery,
} from "./ResourceGroupNotificationsTable.g";

const PAGE_SIZE = 30;

type NotificationNode = NonNullable<
  NonNullable<
    NonNullable<
      ResourceGroupNotificationsQuery["resourceGroupNotifications"]
    >["edges"][number]
  >["node"]
>;

const columnHelper = createColumnHelper<NotificationNode>();

type Props = {
  clientApplicationId: string;
  initialPageIndex?: number;
  pageSize?: number;
  showFooter?: boolean;
  showHeader?: boolean;
  showPageSizeDropdown?: boolean;
};

export const ResourceGroupNotificationsTable = ({
  clientApplicationId,
  initialPageIndex = 0,
  pageSize,
  showFooter = true,
  showHeader = true,
  showPageSizeDropdown = true,
}: Props) => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initialPageIndex,
    pageSize: pageSize || PAGE_SIZE,
  });
  const [sorting, setSorting] = useState<SortingState>([]);

  const notificationsQuery = useResourceGroupNotificationsQuery({
    clientApplicationId,
    first: pagination.pageSize,
    offset: pagination.pageIndex * pagination.pageSize,
    includeResolved: false,
    sortBy:
      sorting.length > 0
        ? `${sorting[0].desc ? "-" : ""}${sorting[0].id}`
        : undefined,
  });

  const { resolveNotification } = useNotification();

  const handlePaginationChange = usePaginationWithParams(
    pagination,
    setPagination,
  );

  const totalCount =
    notificationsQuery.data?.resourceGroupNotifications?.totalCount ??
    undefined;

  const data = useMemo(() => {
    return (
      notificationsQuery.data?.resourceGroupNotifications?.edges
        .map((edge) => edge?.node)
        .filter((node) => !!node) ?? []
    );
  }, [notificationsQuery.data]);

  const handleResolveClick = useCallback(
    async (id: string) => {
      await resolveNotification(id);
    },
    [resolveNotification],
  );

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor("employee.name", {
          header: "Employee",
          id: "employee__name",
          cell: (props) => {
            const employee = props.row.original?.employee;
            if (!employee) {
              return (
                <Text size={14} color="textSecondary">
                  No employee identified
                </Text>
              );
            }
            return (
              <Table.UserCell
                name={employee.name}
                email={employee.email || undefined}
              />
            );
          },
          footer: undefined,
        }),
        columnHelper.accessor("reason", {
          header: "Reason",
          id: "reason",
          cell: (props) => {
            const row = props.row.original;
            return (
              <Text size={14}>
                {getNotificationReasonLabel(row.reason, row.notificationType)}
              </Text>
            );
          },
          footer: undefined,
        }),
        columnHelper.accessor("notificationType", {
          header: "Type",
          id: "notification_type",
          cell: (props) => {
            const value = props.getValue();
            if (!value) {
              return null;
            }
            return (
              <Text>
                <NotificationTypeMappingText notificationType={value} />
              </Text>
            );
          },
          footer: undefined,
        }),
        columnHelper.accessor("createdAt", {
          header: "Created",
          id: "created_at",
          cell: (props) => {
            return <Table.DateCell date={new Date(props.getValue())} />;
          },
        }),
        columnHelper.display({
          id: "actions",
          header: "",
          meta: {
            sticky: true,
            actions: true,
          },
          cell: (props) => {
            return (
              <Box gap={2}>
                <Dropdown
                  trigger={<IconButton size="sm" icon="faEllipsisVertical" />}
                >
                  <Dropdown.Option
                    label="Resolve"
                    icon="faCheck"
                    onSelect={() =>
                      handleResolveClick(props.row.original?.id || "")
                    }
                  />
                </Dropdown>
              </Box>
            );
          },
        }),
      ] as ColumnDef<NotificationNode>[],
    [handleResolveClick],
  );

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setPagination({ pageIndex: 0, pageSize });
  }, []);

  return (
    <Table
      columns={columns}
      data={data}
      pagination={pagination}
      sorting={sorting}
      onPaginationChange={handlePaginationChange}
      onSortingChange={setSorting}
      isLoading={notificationsQuery.isLoading}
      rowCount={totalCount}
      onPageSizeChange={handlePageSizeChange}
      showPageSizeDropdown={showPageSizeDropdown}
      showHeader={showHeader}
      showFooter={showFooter}
    />
  );
};
